import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '../contexts/UserContext';
import {
  PWA_INSTALL_GUIDE_COOLDOWN_MS,
  PWA_INSTALL_MAIN_ROUTES,
  PWA_INSTALL_PROMOTION_DELAY_MS,
  PWA_INSTALL_ROUTE
} from '../constants/pwaInstallConfig';
import { getPwaInstallText } from '../constants/pwaInstallText';
import {
  getPwaInstallRuntimeState,
  markPwaInstallPromotionComplete,
  promptPwaInstall,
  shouldShowPwaInstallPromotion,
  snoozePwaInstallPromotion,
  subscribePwaInstallState
} from '../services/pwaInstallService';
import styles from './PwaInstallPromotion.module.css';

export default function PwaInstallPromotion() {
  const { userInfo } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const primaryButtonRef = useRef(null);
  const [runtime, setRuntime] = useState(getPwaInstallRuntimeState);
  const [isVisible, setIsVisible] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const userId = userInfo?.user_id;
  const text = getPwaInstallText(userInfo?.preferred_language);
  const isMainRoute = PWA_INSTALL_MAIN_ROUTES.includes(location.pathname);

  useEffect(() => subscribePwaInstallState(setRuntime), []);

  useEffect(() => {
    if (!userId || !isMainRoute || runtime.isInstalled || !shouldShowPwaInstallPromotion(userId)) {
      setIsVisible(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setIsVisible(true), PWA_INSTALL_PROMOTION_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [isMainRoute, runtime.isInstalled, userId]);

  useEffect(() => {
    if (runtime.isInstalled && userId) {
      markPwaInstallPromotionComplete(userId);
      setIsVisible(false);
    }
  }, [runtime.isInstalled, userId]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    primaryButtonRef.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        snoozePwaInstallPromotion(userId);
        setIsVisible(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, userId]);

  const openGuide = () => {
    snoozePwaInstallPromotion(userId, PWA_INSTALL_GUIDE_COOLDOWN_MS);
    setIsVisible(false);
    navigate(PWA_INSTALL_ROUTE);
  };

  const handlePrimaryAction = async () => {
    if (!runtime.canPrompt) {
      openGuide();
      return;
    }

    setIsInstalling(true);
    const result = await promptPwaInstall();
    setIsInstalling(false);

    if (result.outcome === 'accepted') {
      markPwaInstallPromotionComplete(userId);
      setIsVisible(false);
      return;
    }

    if (result.outcome === 'unavailable') {
      openGuide();
      return;
    }

    snoozePwaInstallPromotion(userId);
    setIsVisible(false);
  };

  const handleLater = () => {
    snoozePwaInstallPromotion(userId);
    setIsVisible(false);
  };

  const handleAlreadyInstalled = () => {
    markPwaInstallPromotionComplete(userId);
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className={styles.backdrop}>
      <section
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-install-title"
        aria-describedby="pwa-install-description"
      >
        <img className={styles.appIcon} src="/app-icon.png" alt="" />
        <h2 id="pwa-install-title" className={styles.title}>{text.promotionTitle}</h2>
        <p id="pwa-install-description" className={styles.description}>{text.promotionBody}</p>

        <div className={styles.actions}>
          <button
            ref={primaryButtonRef}
            type="button"
            className={styles.primaryButton}
            onClick={handlePrimaryAction}
            disabled={isInstalling}
          >
            {isInstalling ? text.installing : runtime.canPrompt ? text.installNow : text.viewGuide}
          </button>
          <button type="button" className={styles.secondaryButton} onClick={handleLater}>
            {text.later}
          </button>
        </div>

        <button type="button" className={styles.installedButton} onClick={handleAlreadyInstalled}>
          {text.alreadyInstalled}
        </button>
      </section>
    </div>
  );
}
